/**
 * TschaukoRealtimeSurface — shared visual Realtime agent surface.
 *
 * A cinematic alternative to the old-school /chat bubble UI (which stays):
 *   - the character video fills the screen (not a small circle),
 *   - what he says / what you say appears as live SUBTITLES over the video,
 *   - hold-to-talk is one big mic control,
 * so it reads like a video call with the character, not a messenger thread.
 *
 * Both /live and the map dashboard consume this component. They share the
 * same App-level AgentSessionContext and therefore the exact same avatar
 * package, state projection, first-frame contract, subtitles and PTT logic.
 *
 * Host-only chrome stays outside: /live owns fullscreen lifetime, while the
 * map owns backdrop, drag/minimize, close and the optional transcript view.
 */

import type { GuideEntryContext } from '../lib/voice-agent/GuideEntryContext'
import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import BrandLogo from './BrandLogo'
import { useAgentSession } from '../lib/voice-agent/AgentSessionContext'
import type { SessionStatus, TranscriptEntry } from '../lib/voice-agent/VoiceAgentController'
import { useTschaukoVideo, TSCHAUKO_POSTER_URL } from '../lib/voice-agent/useTschaukoVideo'
import {
  overlayForArcturianState,
  resolveArcturianAvatarState,
  type ArcturianAvatarState,
} from '../lib/voice-agent/ArcturianAvatarManifest'
import { fullbodyOverlayForState } from '../lib/voice-agent/ArcturianFullbodyManifest'
import {
  roleForAgentOSAvatarProjection,
  type AgentOSAvatarRole,
  visualStateForAgentOSAvatarProjection,
} from '../lib/voice-agent/AgentOSAvatarRuntime'
import {
  ARCTURIAN_AVATAR_VARIANT_STORAGE_KEY,
  arcturianWebAvatarPackage,
  persistArcturianAvatarVariant,
  resolveArcturianAvatarVariant,
  type ArcturianAvatarVariant,
} from '../lib/voice-agent/ArcturianAvatarVariant'
import { ArcturianAvatarIndicator } from './ArcturianAvatarIndicator'
import { ArcturianFullbodyAvatar } from './ArcturianFullbodyAvatar'
import KeyedOwlAvatar, { type OwlCharacter, type OwlSpeechCue, type OwlState } from './KeyedOwlAvatar'
import BottomSheet, { type SnapPoint } from './BottomSheet'
import { getMapPath } from '../config/project'
import { navigate } from '../lib/routing'
import { useTheme } from '../theme'
import '../tschauko-live.css'

// Species detail — same component /chat uses, rendered here as an immersive
// bottom-sheet overlay over the character video (the agent's show_species tool
// sets focusedKnowledgeId).
const SpeciesView = lazy(() => import('./SpeciesView'))
// Erwaehnt der Erzaehler eine Station (`focus_map`), erscheint ihre
// kanonische Beschreibung — dieselbe Komponente wie in Entdecken.
const EntdeckenFocusedDetail = lazy(() => import('./EntdeckenPage').then((module) => ({
  default: module.EntdeckenFocusedDetail,
})))
// Standortfragen (`where_is`) zeigen KEIN eingebettetes Kartenbild mehr —
// der OSM-Prototyp ist abgeschaltet (Alex 2026-08-17); show_location führt
// auf die echte App-Karte bzw. zeigt hier das Stationsdetail.

const IDLE_STATUSES = new Set<SessionStatus>(['idle', 'closed', 'error'])
const ACTIVE_STATUSES = new Set<SessionStatus>(['connecting', 'ready', 'speaking', 'listening'])
const PTT_KEYS = new Set([' ', 'Spacebar'])

const STATUS_LABEL: Record<SessionStatus, string> = {
  idle: 'Bereit',
  connecting: 'Verbinde …',
  ready: 'Live',
  speaking: 'spricht',
  listening: 'Ich höre Sie',
  error: 'Fehler',
  closed: 'Beendet',
}

/**
 * Rueckweg fuer die vier EINMAL-Zustaende: welche Rolle die Eule zurueckgibt,
 * wenn ihr Clip durchgelaufen ist. Bewusst eine Tabelle statt einer Typ-
 * Behauptung — `idle` und `talk` sind Dauerzustaende und melden nichts, und
 * das soll man hier sehen statt es einem `as` glauben zu muessen.
 */
const OWL_ONE_SHOT_ROLE: Partial<Record<OwlState, AgentOSAvatarRole>> = {
  greeting: 'greeting',
  happy: 'happy',
  joyful: 'joyful',
  sad: 'sad',
}

/** Avatar-Rolle (AgentOS) -> Eulen-Zustand. Die Eule kennt kein
 *  „listening"/„thinking" — beides ist ihr ruhiger Zustand. */
export function owlStateForRole(role: string): OwlState {
  if (role.startsWith('talk_')) return 'talk'
  if (role === 'happy' || role === 'joyful' || role === 'sad' || role === 'greeting') return role
  return 'idle'
}

/** Latest thing said — becomes the on-screen subtitle. */
function latestUtterance(transcript: TranscriptEntry[]): TranscriptEntry | null {
  for (let i = transcript.length - 1; i >= 0; i--) {
    if (transcript[i].text.trim()) return transcript[i]
  }
  return null
}

function MidshotAvatar({ state, sessionActive }: { state: ArcturianAvatarState; sessionActive: boolean }) {
  const videoRefA = useRef<HTMLVideoElement>(null)
  const videoRefB = useRef<HTMLVideoElement>(null)
  const [failed, setFailed] = useState(false)
  useTschaukoVideo([videoRefA, videoRefB], state, sessionActive, failed, setFailed)

  return (
    <div className="tl-midshot" data-avatar-variant="midshot">
      <img className="tl-video tl-video--base" src={TSCHAUKO_POSTER_URL} alt="Dr. Peter Tschauko" aria-hidden="true" />
      {!failed && (
        <>
        <video
          ref={videoRefA}
          className="tl-video tl-video--panel"
          data-avatar-video-panel="0"
          poster={TSCHAUKO_POSTER_URL}
          muted
          playsInline
          preload="auto"
          aria-hidden="true"
        />
        <video
          ref={videoRefB}
          className="tl-video tl-video--panel"
          data-avatar-video-panel="1"
          poster={TSCHAUKO_POSTER_URL}
          muted
          playsInline
          preload="auto"
          aria-hidden="true"
        />
        </>
      )}
    </div>
  )
}

function initialAvatarVariant(): ArcturianAvatarVariant {
  if (typeof window === 'undefined') return 'midshot'
  let stored: string | null = null
  try {
    stored = window.localStorage.getItem(ARCTURIAN_AVATAR_VARIANT_STORAGE_KEY)
  } catch {
    // Private WebViews can disable storage; the query/default still works.
  }
  return resolveArcturianAvatarVariant(window.location.search, stored)
}

export interface TschaukoRealtimeSurfaceProps {
  /** Fullscreen route owns the session lifetime; the dashboard host only
   *  presents the already App-scoped session and may unmount on minimize. */
  presentation: 'fullscreen' | 'dashboard'
  /** Hosts may pin one complete avatar package. `/live` stays selectable;
   *  the compact map dashboard deliberately stays on the opaque MIDSHOT. */
  avatarVariantMode: 'selectable' | ArcturianAvatarVariant
  /** Statt der Menschen-Figur die freigestellte EULE zeigen (Alex: eigene
   *  Realtime-Variante fuer Tschauka, /live-tschauka). Setzt die Buehne auf
   *  KeyedOwlAvatar — dieselben Chroma-Clips, die Story + Annotationen
   *  nutzen —, blendet den Avatar-Test-Umschalter aus und benennt die
   *  Figur in Status/Untertitel um. Die Realtime-Sitzung selbst bleibt die
   *  App-weite (eine Stimme) — eine eigene Tschauka-STIMME waere ein
   *  zweiter Controller und damit ein eigener Schritt. */
  owlCharacter?: OwlCharacter
  /** Wie nah die Eule steht (1 = wie erzeugt). Kommt aus der Figur. */
  owlZoom?: number
  /** Sichtbares Zeichen beim Sprechen. Kommt aus der Figur. */
  owlSpeechCue?: OwlSpeechCue
  /** Anzeigename der Figur (Status/Untertitel). Default Dr. Tschauko. */
  speakerName?: string
  /** Optionaler, host-eigener Start der räumlichen XR-Projektion auf
   *  derselben Live-Seite. Der Surface navigiert dafür nie auf eine zweite
   *  Route und besitzt selbst keine WebXR-Ressourcen. */
  onEnterXr?: () => void
  /** Einstiegskontext, mit dem „Gespräch starten" öffnet (z. B. die Art aus
   *  der Arten-AR). Ohne: allgemeine Begrüßung wie bisher. */
  entryContext?: GuideEntryContext | null
}

export function TschaukoRealtimeSurface({
  presentation,
  avatarVariantMode,
  owlCharacter,
  owlZoom = 1,
  owlSpeechCue = 'none',
  speakerName = 'Dr. Tschauko',
  onEnterXr,
  entryContext = null,
}: TschaukoRealtimeSurfaceProps) {
  const { snapshot, controller, hasSpoken } = useAgentSession()
  const { skyMode } = useTheme()
  const fullscreen = presentation === 'fullscreen'
  const [selectableAvatarVariant, setSelectableAvatarVariant] = useState<ArcturianAvatarVariant>(initialAvatarVariant)
  const avatarVariant = avatarVariantMode === 'selectable'
    ? selectableAvatarVariant
    : avatarVariantMode
  const [detailSheetSnap, setDetailSheetSnap] = useState<SnapPoint>('expanded')
  const avatarPackage = arcturianWebAvatarPackage(avatarVariant)

  const isIdle = IDLE_STATUSES.has(snapshot.status)
  const isActive = ACTIVE_STATUSES.has(snapshot.status)
  const isSpecies = snapshot.focusedKnowledgeId != null
  const focusedWaypointId = snapshot.focusedLocation?.waypointId ?? null
  const isLocationDetail = focusedWaypointId != null
  const isDetail = isSpecies || isLocationDetail
  /* `ready` bedeutet nur: WebRTC/DataChannel stehen. Bei einer normalen
     Oeffnung ist der Fuehrer fuer den Besucher erst wirklich ansprechbar,
     sobald sein erster Ton begonnen hat. Der Context fuehrt diesen Riegel
     bereits sitzungsweit; die grosse Oberflaeche hat ihn bisher ignoriert
     und zeigte den Mikrofonknopf dadurch zu frueh. Eine explizit stille
     Oeffnung ist die einzige Ausnahme — dort kommt absichtlich kein Gruß. */
  const canTalk = hasSpoken
    || snapshot.status === 'speaking'
    || (snapshot.status === 'ready' && snapshot.entryContext?.opening === 'wait_silently')
  const isWarmingUp = isActive && !canTalk

  const legacyAvatarState = resolveArcturianAvatarState({
    status: snapshot.status,
    activeTool: snapshot.activeTool,
    isMicActive: snapshot.isMicActive,
  })
  const avatarRole = snapshot.avatarProjection
    ? roleForAgentOSAvatarProjection(snapshot.avatarProjection)
    : 'idle'
  const avatarState = avatarVariant === 'fullbody' && snapshot.avatarProjection
    ? visualStateForAgentOSAvatarProjection(snapshot.avatarProjection)
    : legacyAvatarState
  const avatarIndicator = avatarVariant === 'fullbody'
    ? fullbodyOverlayForState(avatarState)
    : overlayForArcturianState(avatarState)

  useEffect(() => {
    if (avatarVariantMode === 'selectable') {
      persistArcturianAvatarVariant(selectableAvatarVariant)
    }
  }, [avatarVariantMode, selectableAvatarVariant])

  // Spacebar push-to-talk (hold).
  useEffect(() => {
    const isTextField = (el: EventTarget | null) =>
      el instanceof HTMLElement &&
      (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)
    const down = (e: KeyboardEvent) => {
      if (!canTalk || isDetail || !PTT_KEYS.has(e.key) || e.repeat || isTextField(e.target)) return
      e.preventDefault()
      controller.setMicActive(true)
    }
    const up = (e: KeyboardEvent) => {
      if (!PTT_KEYS.has(e.key) || isTextField(e.target)) return
      e.preventDefault()
      controller.setMicActive(false)
    }
    window.addEventListener('keydown', down)
    window.addEventListener('keyup', up)
    return () => {
      window.removeEventListener('keydown', down)
      window.removeEventListener('keyup', up)
    }
  }, [controller, isDetail, canTalk])

  // A canonical detail is modal over the Realtime surface. If it opens while
  // the user is still holding PTT, release the microphone immediately; the
  // detail must be closed/collapsed before another recording can start.
  useEffect(() => {
    if (isDetail && controller.getSnapshot().isMicActive) {
      controller.setMicActive(false)
    }
  }, [controller, isDetail])

  /* Issue #479: Sabrina hat den Start-Knopf gehalten wie ein Mikrofon. Die
     Form trennt die beiden jetzt (Pille vs. runder Mikro-Knopf) — aber wer
     trotzdem hält, soll nicht ins Leere greifen: der gedrückte Finger wird
     über das Verbinden hinweg gemerkt und geht, sobald die Sitzung steht,
     direkt ins Sprechen über. Ein kurzer Tipp startet nur die Sitzung. */
  const [startHolding, setStartHolding] = useState(false)
  /* Das Loslassen hängt am FENSTER, nicht am Knopf: sobald `open()` läuft,
     ist die Seite nicht mehr idle und die Start-Pille wird ausgehängt —
     mitten in der Geste. Ein `pointerup` auf einem entfernten Element kann
     ausbleiben, und dann liefe das Mikrofon endlos weiter. Am Fenster kommt
     das Loslassen in jedem Fall an. */
  useEffect(() => {
    if (!startHolding) return
    const end = () => {
      setStartHolding(false)
      if (controller.getSnapshot().isMicActive) controller.setMicActive(false)
    }
    window.addEventListener('pointerup', end)
    window.addEventListener('pointercancel', end)
    return () => {
      window.removeEventListener('pointerup', end)
      window.removeEventListener('pointercancel', end)
    }
  }, [startHolding, controller])
  useEffect(() => {
    if (!startHolding) return
    // Der transportseitige `ready`-Status reicht nicht: bei einer normalen
    // Oeffnung muss erst der erste wirklich abgespielte Agententon den Riegel
    // oeffnen. Sonst spricht der Besucher in die laufende Begruessung hinein.
    if (canTalk && !snapshot.isMicActive) controller.setMicActive(true)
  }, [startHolding, canTalk, snapshot.isMicActive, controller])

  // Only the fullscreen route owns session lifetime. The dashboard surface
  // unmounts on minimize and when switching to transcript; neither action may
  // terminate the shared App-level controller.
  useEffect(() => {
    if (!fullscreen) return
    return () => {
      if (!IDLE_STATUSES.has(controller.getSnapshot().status)) controller.close()
    }
  }, [controller, fullscreen])

  const sub = latestUtterance(snapshot.transcript)
  const hasHistory = snapshot.transcript.length > 0
  const requiresBrowserRestart = snapshot.errorRecovery === 'restart_browser'
  /* Kurz und als Handlung formuliert — der Text sitzt jetzt IN der Pille
     (Issue #479), nicht mehr als Zeile unter einem runden Knopf. */
  const heroLabel = snapshot.status === 'error'
    ? requiresBrowserRestart ? 'Seite neu laden' : 'Erneut versuchen'
    : hasHistory
      ? 'Gespräch fortsetzen'
      : 'Gespräch starten'

  const pttDisabled = !canTalk || isDetail
  const startOrRecover = (): void => {
    if (requiresBrowserRestart) {
      window.location.reload()
      return
    }
    setStartHolding(true)
    void controller.open(entryContext ?? undefined)
  }

  return (
    <div
      className={`tl-root${fullscreen ? '' : ' tl-root--dashboard'}`}
      data-realtime-surface={presentation}
    >
      {/* Marke links oben (Issue #388) — auf der Live-Seite lag bisher keine.
          Nur im Ruhezustand: sobald die Session läuft, sitzt dort die
          Status-Pille der .tl-topbar (top 14px / left 16px), die beiden würden
          einander sonst überlagern. Klick-durchlässig, damit der Tap-to-talk
          auf die ganze Fläche wirkt. */}
      {fullscreen && isIdle && <BrandLogo
        lockup
        style={{
          position: 'absolute', top: 'calc(12px + env(safe-area-inset-top, 0px))', left: 16,
          zIndex: 20, pointerEvents: 'none',
          /* Weiß auf dem dunklen Video — die Marke ist eine Maske und nimmt
             `currentColor`, deshalb reicht `color`. Der Schatten hebt sie von
             wechselnden Bildinhalten ab. */
          color: 'var(--ar-white, #F7F5F0)',
          filter: 'drop-shadow(0 1px 6px rgba(0,0,0,0.55))',
        }}
        size={26}
      />}
      {/* Fullscreen character video (A/B panels; poster only at startup/failure). */}
      {/* `data-compact` schaltet die Buehne um, sobald ein Detail-Blatt offen
          ist: die Figur rueckt klein nach oben in den freien Streifen ueber
          dem Blatt, statt dahinter zu verschwinden (Alex 2026-08-04,
          Bildschirmfoto 117435 — die Eule war hinter der Krainer Lilie
          vollstaendig verdeckt). Gilt fuer BEIDE Figuren, Eule wie Mensch. */}
      <div
        className="tl-stage"
        data-avatar-package={avatarPackage.package_id}
        data-compact={isDetail ? 'true' : undefined}
      >
        {owlCharacter ? (
          /* Eulen-Buehne: dieselbe Chroma-Key-Komponente wie Story und
             Annotationen (KeyedOwlAvatar), gross und mittig ueber dunklem
             Grund. Rollen der Avatar-Projektion auf die Eulen-Zustaende
             abgebildet — Zuhoeren/Nachdenken sind fuer die Eule 'idle'. */
          <div className="tl-owl-stage">
            <KeyedOwlAvatar
              character={owlCharacter}
              state={owlStateForRole(avatarRole)}
              talkVariant={avatarRole === 'talk_medium' ? 1 : avatarRole === 'talk_long' ? 2 : 0}
              zoom={owlZoom}
              speechCue={owlSpeechCue}
              /* Der Pegel der AUSGABE — die Stimme der Figur, nicht das
                 Mikrofon. Als Abfrage uebergeben, damit die Zeichenschleife
                 ihn je Bild holt, ohne dass React neu zeichnet. */
              speechLevel={controller.getOutputLevel}
              /* KEINE feste Groesse: 520 px sassen als Inline-Angabe ueber der
                 Klasse und ragten auf einem 390-px-Telefon rechts hinaus. Die
                 Klasse rechnet mit min(74vw, 46vh) und bleibt im Bild. */
              className="tl-owl-stage__avatar"
              /* Dieselben zwei Rueckmeldungen wie beim Menschen-Avatar — ohne
                 sie ist die Zustandsmaschine nur halb angeschlossen: der
                 Controller wartet auf das erste Bild, bevor er gruessen laesst,
                 und auf das Ende eines Einmal-Clips, bevor er die Rolle
                 freigibt. Die Eule hat beides bis heute nie gemeldet. */
              onFirstFrame={controller.avatarDidDisplayFirstFrame}
              onClipEnded={(owlState) => {
                const role = OWL_ONE_SHOT_ROLE[owlState]
                if (role) controller.avatarTransientRoleDidFinish(role)
              }}
            />
          </div>
        ) : avatarPackage.renderer === 'green-canvas' ? (
          <ArcturianFullbodyAvatar
            key={snapshot.avatarProjection?.session_id ?? 'avatar-pending'}
            role={avatarRole}
            sessionActive={isActive && snapshot.avatarProjection != null}
            onFirstFrameReady={controller.avatarDidDisplayFirstFrame}
            onTransientRoleEnded={controller.avatarTransientRoleDidFinish}
          />
        ) : (
          <MidshotAvatar state={avatarState} sessionActive={isActive} />
        )}
        <ArcturianAvatarIndicator
          state={avatarState}
          indicator={avatarIndicator}
        />
        {/* Legibility scrims — top for status, bottom for subtitle + control. */}
        <div className="tl-scrim tl-scrim--top" />
        <div className="tl-scrim tl-scrim--bottom" />
      </div>

      {avatarVariantMode === 'selectable' && (
      <div className="tl-avatar-variant" role="group" aria-label="Avatar-Darstellung testen">
        <span className="tl-avatar-variant__caption">Avatar-Test</span>
        <button
          type="button"
          className={avatarVariant === 'midshot' ? 'is-active' : ''}
          aria-pressed={avatarVariant === 'midshot'}
          onClick={() => setSelectableAvatarVariant('midshot')}
        >
          Brustbild
        </button>
        <button
          type="button"
          className={avatarVariant === 'fullbody' ? 'is-active' : ''}
          aria-pressed={avatarVariant === 'fullbody'}
          onClick={() => setSelectableAvatarVariant('fullbody')}
        >
          Ganzkörper
        </button>
      </div>
      )}

      {fullscreen && isIdle && onEnterXr && (
        <button
          type="button"
          className="tl-ar-entry tl-ar-entry--idle"
          onClick={onEnterXr}
        >
          <span aria-hidden="true">◉</span>
          Im Raum erleben
        </button>
      )}

      {/* Top bar: status + close. */}
      {!isIdle && (
        <div className="tl-topbar">
          <span className={`tl-status tl-status--${isWarmingUp ? 'connecting' : snapshot.status}`}>
            <span className="tl-status__dot" />
            {isWarmingUp
              ? 'Wird verbunden …'
              : snapshot.status === 'speaking' ? `${speakerName} spricht` : STATUS_LABEL[snapshot.status]}
          </span>
          {fullscreen && (
            <div className="tl-topbar__actions">
              {onEnterXr && (
                <button
                  type="button"
                  className="tl-ar-entry"
                  onClick={onEnterXr}
                >
                  <span aria-hidden="true">◉</span>
                  Im Raum erleben
                </button>
              )}
              <button
                type="button"
                className="tl-close"
                onClick={() => controller.close()}
                aria-label="Live-Ansicht beenden"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M6 6l12 12M18 6 6 18" />
                </svg>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Error banner. */}
      {snapshot.errorMessage && (
        <div className="tl-error" role="alert">{snapshot.errorMessage}</div>
      )}

      {/* Der kleine Punkt in der Ecke wurde beim Feldtest uebersehen. Dieser
          zentrale, ruhige Indikator bleibt so lange stehen, bis nicht nur die
          Leitung, sondern der erste nutzbare Sprachturn wirklich da ist. */}
      {isWarmingUp && (
        <div className="tl-connecting" role="status" aria-live="polite" aria-label="Realtime-Guide wird verbunden">
          <span className="tl-connecting__spinner" aria-hidden="true" />
          <span className="tl-connecting__label">{speakerName} wird verbunden …</span>
          <span className="tl-connecting__hint">Bitte einen Augenblick warten</span>
        </div>
      )}

      {/* Subtitle — the live caption of the latest utterance (hidden while a
          species panel is up; the panel is the focus then). */}
      {isActive && sub && !isDetail && (
        <div className={`tl-subtitle tl-subtitle--${sub.speaker}${sub.partial ? ' tl-subtitle--partial' : ''}`} aria-live="polite">
          <span className="tl-subtitle__who">{sub.speaker === 'bot' ? speakerName : 'Sie'}</span>
          <span className="tl-subtitle__text">{sub.text}</span>
        </div>
      )}
      {isActive && !sub && !isDetail && snapshot.status !== 'connecting' && (
        <div className="tl-hint">Halten Sie den Knopf gedrückt und stellen Sie Ihre Frage.</div>
      )}

      {/* Agent-driven species and location details. Both reuse canonical
          detail components inside the same dashboard BottomSheet contract. */}
      {isDetail && (
        <BottomSheet
          className="tl-species-sheet"
          snap={detailSheetSnap}
          onSnapChange={(snap) => {
            if (snap === 'collapsed') {
              if (isSpecies) controller.clearFocusedKnowledge()
              else controller.clearFocusedLocation()
              return
            }
            setDetailSheetSnap(snap)
          }}
          dark={skyMode === 'night'}
          floatingDesktop={fullscreen}
          positionMode="absolute"
          disableHalf
          collapsedHeight={80}
          expandedRatio={0.80}
          zIndex={6}
          collapsedContent={
            <button
              type="button"
              className="tl-species__close"
              onClick={() => {
                if (isSpecies) controller.clearFocusedKnowledge()
                else controller.clearFocusedLocation()
              }}
              aria-label={isSpecies ? 'Art-Detail schließen' : 'Ortsdetail schließen'}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M6 9l6 6 6-6" />
              </svg>
              <span>schließen</span>
            </button>
          }
          expandedContent={
            <div role="dialog" aria-label={isSpecies ? 'Wissensobjekt' : 'Ortsdetail'}>
              <Suspense fallback={<div className="tl-species__loading">Lade Darstellung …</div>}>
                {isSpecies ? (
                  <SpeciesView knowledgeId={snapshot.focusedKnowledgeId ?? undefined} embedded />
                ) : (
                  <EntdeckenFocusedDetail
                    waypointId={focusedWaypointId!}
                    dark={skyMode === 'night'}
                    onClose={() => controller.clearFocusedLocation()}
                    onShowOnMap={() => {
                      const waypointId = controller.getSnapshot().focusedLocation?.waypointId
                      if (waypointId == null) return
                      controller.clearFocusedLocation()
                      // close() raeumt Spuren, DataChannel und Fernaudio
                      // synchron ab, BEVOR die Route wechselt.
                      if (fullscreen) controller.close()
                      navigate(`${getMapPath()}?focus=${waypointId}&from=discover`)
                    }}
                  />
                )}
              </Suspense>
            </div>
          }
        />
      )}

      {/* Bottom control: hero CTA (idle) OR push-to-talk (active).
          Issue #479 (Sabrina): beide saßen als gold-runder Knopf an derselben
          Stelle — sie hat den Start-Knopf GEDRÜCKT GEHALTEN, weil sie ihn für
          das Mikrofon hielt, und nichts geschah. Der Start ist jetzt eine
          BREITE PILLE, das Mikrofon bleibt der einzige runde Knopf: die Form
          unterscheidet die beiden, nicht nur das Symbol. Zusätzlich ist die
          Verwechslung folgenlos gemacht — halten startet und geht direkt ins
          Sprechen über (holdIntentRef). */}
      <div
        className={`tl-controls${isDetail ? ' tl-controls--blocked' : ''}`}
        aria-hidden={isDetail}
        inert={isDetail}
      >
        {isIdle ? (
          <>
          <button
            type="button"
            className="tl-start"
            /* Start hängt am pointerdown, NICHT am click: click feuert erst
               beim Loslassen — wer den Knopf hält (genau Sabrinas Geste),
               würde sonst warten, bis er wieder loslässt. */
            onPointerDown={startOrRecover}
            /* Tastatur (Enter/Space) erzeugt einen Click ohne Pointer —
               `detail === 0` unterscheidet ihn vom Maus-/Finger-Click, den
               pointerdown oben schon bedient hat. */
            onClick={(e) => {
              if (e.detail !== 0) return
              if (requiresBrowserRestart) window.location.reload()
              else void controller.open(entryContext ?? undefined)
            }}
          >
            <span className="tl-start__ring" />
            <span className="tl-start__label">{heroLabel}</span>
          </button>
          {/* Sagt vorab, was NACH dem Start gilt — genau das Wissen, das
              Sabrina fehlte, als sie den Start-Knopf gedrückt hielt. */}
          <span className="tl-start__hint">Danach: Knopf halten zum Sprechen</span>
          </>
        ) : (
          <button
            type="button"
            draggable={false}
            className={`tl-ptt${snapshot.isMicActive ? ' tl-ptt--active' : ''}`}
            disabled={pttDisabled}
            onContextMenu={(e) => e.preventDefault()}
            onMouseDown={(e) => e.preventDefault()}
            onPointerDown={(e) => {
              e.preventDefault()
              try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId) } catch { /* noop */ }
              controller.setMicActive(true)
            }}
            onPointerUp={(e) => {
              try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId) } catch { /* noop */ }
              controller.setMicActive(false)
            }}
            onPointerCancel={() => controller.setMicActive(false)}
            aria-pressed={snapshot.isMicActive}
            aria-label="Halten zum Sprechen"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="9" y="3" width="6" height="12" rx="3" />
              <path d="M5 11a7 7 0 0 0 14 0" />
              <line x1="12" y1="18" x2="12" y2="22" />
            </svg>
            <span className="tl-ptt__label">{snapshot.isMicActive ? 'Ich höre …' : 'Halten zum Sprechen'}</span>
          </button>
        )}
      </div>
    </div>
  )
}
